import { formatAnalysisMoney } from "@/revenue-intelligence/lib/moneyDisplay";
import { defineRevenueIntelligenceAnalysisContract } from "../lib/revenue-intelligence-analysis-contract";
import type { ForecastChartRow } from "./ForecastChart";
import { getStateOfSalesV2ForecastPosition } from "./forecastPosition";
import {
  buildStateOfSalesForecastChatMetrics,
  buildStateOfSalesForecastTimelineChatDataset,
} from "./stateOfSalesChatContracts";
import type { useStateOfSalesV2Forecast } from "./useForecastData";
import { stateOfSalesChatPeriodOptions } from "./stateOfSalesChatScope";

type ForecastModel = ReturnType<typeof useStateOfSalesV2Forecast>;
type ForecastData = NonNullable<ForecastModel["forecast"]>;

function getForecastPeriodLabel(period: ForecastModel["period"]) {
  return stateOfSalesChatPeriodOptions.find(option => option.value === period)?.label
    ?? period;
}

export function getForecastSummary(
  forecast: ForecastData,
  position: ReturnType<typeof getStateOfSalesV2ForecastPosition>,
  companyCurrency: ForecastModel["companyCurrency"],
  movementComparable = true,
) {
  const topDrivers = forecast.top3Risks;
  const movement = movementComparable
    && !position.historical
    && forecast.forecastSnapshot.available
    ? position.forecast - forecast.forecastSnapshot.forecast
    : null;
  const subject = position.historical ? "Won" : "Forecast";
  const positionClause = position.gapToTarget < 0
    ? `${formatAnalysisMoney(Math.abs(position.gapToTarget), companyCurrency)} below target`
    : position.gapToTarget > 0
      ? `${formatAnalysisMoney(position.gapToTarget, companyCurrency)} above target`
      : "on target";
  const movementClause = movement === null
    ? null
    : movement < 0
      ? `down ${formatAnalysisMoney(Math.abs(movement), companyCurrency)} in 30 days`
      : movement > 0
        ? `up ${formatAnalysisMoney(movement, companyCurrency)} in 30 days`
        : "unchanged in 30 days";
  const conclusion = position.historical || !movementClause
    ? `${subject} is ${positionClause}.`
    : `${subject} is ${positionClause}, ${movementClause}.`;

  const explanation = position.historical
    ? ""
    : movement === null
      ? "30-day movement is unavailable."
      : movement === 0
        ? "No material 30-day movement."
        : "30-day movement changed the current Forecast position.";

  const evidence = position.historical
    ? `Won: ${formatAnalysisMoney(position.won, companyCurrency)}. Target: ${formatAnalysisMoney(position.target, companyCurrency)}.`
    : position.future && position.won === 0
      ? `Forecast currently consists of ${formatAnalysisMoney(position.expectedFromOpenDeals, companyCurrency)} expected from open deals.`
      : `Forecast combines ${formatAnalysisMoney(position.won, companyCurrency)} Won with ${formatAnalysisMoney(position.expectedFromOpenDeals, companyCurrency)} expected from open deals.`;

  return {
    conclusion,
    evidence,
    explanation,
    movement,
    topDrivers,
  };
}

export function buildForecastOverviewChatContract({
  accumulated,
  chartData,
  companyCurrency,
  companyId,
  filtersLabel,
  forecast,
  period,
  movementComparable = true,
}: {
  accumulated: boolean;
  chartData: ForecastChartRow[];
  companyCurrency: ForecastModel["companyCurrency"];
  companyId: string;
  filtersLabel?: string;
  forecast: ForecastData;
  period: ForecastModel["period"];
  movementComparable?: boolean;
}) {
  const position = getStateOfSalesV2ForecastPosition(
    forecast,
    period,
    accumulated,
    chartData,
  );
  const summary = getForecastSummary(
    forecast,
    position,
    companyCurrency,
    movementComparable,
  );
  return defineRevenueIntelligenceAnalysisContract({
    contractId: "state_of_sales.forecast.overview",
    analysisLabel: "State of Sales Forecast overview",
    accessPolicy: {
      allowedRoles: ["super_admin", "admin"],
      companyId,
      dataScope: "company",
      enforcement: "company_role_gate",
    },
    productLens: "Where the selected-period Forecast is expected to land.",
    requiredAccess: "revenue_intelligence.forecast",
    sourceHelper: "getForecastSummary",
    scope: {
      pageLabel: "State of Sales",
      periodLabel: getForecastPeriodLabel(period),
      filtersLabel,
    },
    metrics: buildStateOfSalesForecastChatMetrics({
      companyCurrency,
      historical: position.historical,
      metricIdPrefix: "state_of_sales.forecast.overview",
      position,
    }),
    chat: {
      availability: "available",
      datasets: [
        buildStateOfSalesForecastTimelineChatDataset(
          chartData,
          "state_of_sales.forecast.overview",
        ),
      ],
      topics: ["forecast", "forecast position", "prognos"],
      suggestedQuestions: [
        "What needs management attention?",
        "Which deals have the greatest risk?",
        "How is Forecast calculated?",
      ],
      insights: [{
        insightId: "state_of_sales.forecast.overview",
        label: "Forecast position",
        title: summary.conclusion,
        body: summary.explanation || summary.evidence,
        evidence: summary.evidence,
        sourceFields: ["forecastPosition", "forecastSnapshot"],
      }],
    },
  });
}
